import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef
} from 'react';
import { ToastContainer, Slide } from 'react-toastify';
import {
  useParams,
  Outlet,
  useNavigate,
  useSearchParams
} from 'react-router-dom';
import { Button, Layout, message } from 'antd';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { PlayerProvider } from '@context/PlayerContext';
import {
  setAssetVariant,
  removeComment,
  editComment,
  setAsset,
  setAssetsSliderAnchor,
  setCommentLike,
  setCommentResolve
} from '@redux/actions/mediaViewerAction';
import { useCurrentWorkspace } from '@hooks/workspace';
import MediaViewerHeader from '@components/MediaViewerHeader';
import { MediaViewerActionTypes } from '@redux/types/mediaViewerType';
import {
  CreateAssetVersionCommentDto,
  GetAssetVersionCommentsSuccessDto,
  ReplyAssetVersionCommentDto,
  GetAssetVersionTasksLastReadSuccessDto
} from '@api/Api';
import {
  assetInternalCommentsCreateAssetComment,
  assetInternalCommentsDeleteAssetComment,
  assetInternalCommentsGetAssetComments,
  assetInternalCommentsListCommentPotentialMentions,
  assetInternalCommentsMarkAssetCommentsRead,
  assetInternalCommentsReplyAssetComment,
  assetInternalCommentsSetAssetCommentLike,
  assetInternalCommentsSetAssetCommentResolve,
  assetInternalCommentsUpdateAssetComment,
  assetSetRecentlyOpened
} from '@api/Asset';
import { useTypedSelector } from '@hooks';
import {
  useWebSocketDebouncedSubscription,
  useWebSocketSubscription
} from '@hooks/useWebSocketClient';
import { MixpanelService, MixpanelEventType } from '@services/mixpanelService';
import { useAuth } from '@hooks/useAuth';
import { toggleApprovalModal } from '@redux/actions/modalAction';
import { apiClient } from '@api/client';
import type { MediaViewerHeaderProps } from '@components/MediaViewerHeader/MediaViewerHeader';
import { useAssetQuery } from '@hooks/queries/assets';
import {
  QUERY_COMMENTS_ASSETS,
  useInternalCommentsAssetReviewQuery
} from '@hooks/queries/asset';
import {
  QUERY_TASKS_LAST_READ,
  useAssetVersionTasksLastReadQuery
} from '@hooks/queries/task';
import { taskMarkAssetVersionTasksRead } from '@api/Task';
import { MediaViewerOutletContextState } from '@layouts/types';
import { useIsDemoOrganization } from '@hooks/useIsDemoOrganization';
import DemoModePanel, {
  DEMO_MODE_PANEL_HEIGHT
} from '@components/DemoModePanel/DemoModePanel';
import MediaViewerTourBanner from '@components/MediaViewerTourBanner/MediaViewerTourBanner';
import { useLayoutEffectWithPrev } from '@hooks/useEffectWithPrev';
import { showTranscriptionReadyToast } from '@helpers/showTranscriptionReadyToast';
import { showNewAssetVersionToast } from '@helpers/showNewAssetVersionToast';
import { nonNull } from '@helpers/non-null';
import {
  mediaViewerPath,
  UXP_ADD_PREMIERE_MARKER,
  closeEmbeddedMediaViewer
} from '@helpers/uxpEmbeddedMediaViewer';
import {
  setDesktopViewport,
  useResponsive,
  useResetMobileViewport
} from '@hooks/useResponsive';
import { useThemeColor } from '@hooks/useThemeColor';
import '@pages/MediaViewer/MediaViewer.less';
import { IS_UXP_WEBVIEW } from '@config/constants';
import { ErrorIcon } from '@icons';
import { AssetsSliderProvider } from '@pages/MediaViewer/components/AssetsSlider/AssetsSliderProvider';

const { Header, Content } = Layout;

type MediaViewerLayoutProps = {
  headerProps?: MediaViewerHeaderProps;
};

function MediaViewerLayout({ headerProps = {} }: MediaViewerLayoutProps) {
  const { assetId, versionId: versionIdParam } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isLgWidth } = useResponsive();
  const isDemoOrganization = useIsDemoOrganization();
  const [searchParams] = useSearchParams();
  const isEmbeddedWebView = IS_UXP_WEBVIEW;

  const { user } = useAuth(false, 'internal');
  const [currentWorkspace, setCurrentWorkspace] = useCurrentWorkspace(false);
  const selectedVersion = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.selectedVersion
  );
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  const startTour = searchParams.get('startTour');
  const isAnnouncementTour = [
    'announcement-media-viewer',
    'announcement-media-viewer-mobile'
  ].includes(startTour || '');
  const shouldUseDesktopViewport = isDemoOrganization && !isAnnouncementTour;

  useResetMobileViewport({
    enabled: !shouldUseDesktopViewport && (!startTour || isAnnouncementTour)
  });

  useLayoutEffect(() => {
    if (!shouldUseDesktopViewport) return;
    setDesktopViewport();
  }, [shouldUseDesktopViewport]);

  const queryClient = useQueryClient();
  const assetVersionId = selectedVersion?.id;
  const prevVersionId = useRef(assetVersionId || null);
  const containerHeight =
    'calc(var(--viewport-min-height, 100vh) - var(--demo-mode-panel-width, 0px))';
  const backgroundColor = isLgWidth ? '#1A1C1E' : '#000';

  useThemeColor(backgroundColor);

  const { data: commentsList, isLoading: isLoadingCommentsList } =
    useInternalCommentsAssetReviewQuery(
      {
        assetVersionId: assetVersionId || ''
      },
      {
        keepPreviousData: assetVersionId === prevVersionId.current,
        enabled: !!assetVersionId
      }
    );
  const commentsLastReadAt = commentsList?.lastReadAt ?? null;
  const commentsItems = commentsList?.comments ?? [];

  const { data: tasksLastReadData } = useAssetVersionTasksLastReadQuery(
    { assetVersionId: assetVersionId || '' },
    { enabled: !!assetVersionId }
  );
  const tasksLastReadAt = tasksLastReadData?.lastReadAt ?? null;
  const latestTaskCreatedAt = tasksLastReadData?.latestCreatedAt ?? null;

  if (assetVersionId !== prevVersionId.current && !isLoadingCommentsList) {
    prevVersionId.current = assetVersionId || null;
  }

  useEffect(() => {
    if (!assetId || !assetVersionId) return;
    (async () => {
      await assetSetRecentlyOpened({ assetId, assetVersionId });
    })();
  }, [assetId, assetVersionId]);

  const outletContext = useMemo<MediaViewerOutletContextState>(
    () => ({
      fetchCommentsList: async ({ assetVersionId }) => {
        const { data } = await assetInternalCommentsGetAssetComments({
          assetVersionId
        });
        return data;
      },
      addNewComment: async (data, parentId) => {
        try {
          let newComment = null;
          if (parentId && parentId !== 'new') {
            const {
              data: { comment }
            } = await assetInternalCommentsReplyAssetComment(
              data as ReplyAssetVersionCommentDto
            );
            newComment = comment;
          } else {
            const {
              data: { comment }
            } = await assetInternalCommentsCreateAssetComment(
              data as CreateAssetVersionCommentDto
            );
            newComment = comment;
            if (user) {
              MixpanelService.track(user.id, MixpanelEventType.MANAGE_ASSETS, {
                addToFavorite: false,
                deleteAsset: false,
                newAsset: false,
                externalLinkShare: false,
                approvalRouting: false,
                newCommentAsset: true,
                annotationLeft: !!comment.annotations,
                taskFromComment: false
              });
            }
          }

          if (
            isEmbeddedWebView &&
            newComment?.timeCode?.fromSeconds != null &&
            Number.isFinite(newComment.timeCode.fromSeconds)
          ) {
            const { uxpHost } = window as unknown as {
              uxpHost?: { postMessage: (msg: object) => void };
            };
            uxpHost?.postMessage({
              type: UXP_ADD_PREMIERE_MARKER,
              assetId,
              commentId: newComment.id,
              text: newComment.text,
              fromSeconds: newComment.timeCode.fromSeconds,
              toSeconds: newComment.timeCode.toSeconds ?? null
            });
          }

          return newComment;
        } catch (error: any) {
          message.error({
            content:
              error?.response?.data?.message ||
              'Failed to create comment. Please try again.',
            className: 'message-dark-modal'
          });
          throw error;
        }
      },
      removeComment: async (id) => {
        try {
          await removeComment(id);
          await assetInternalCommentsDeleteAssetComment({ id });
        } catch (error: any) {
          message.error({
            content:
              error?.response?.data?.message ||
              'Failed to delete comment. Please try again.',
            className: 'message-dark-modal'
          });
        }
      },
      editComment: async (data) => {
        try {
          const {
            data: { comment }
          } = await assetInternalCommentsUpdateAssetComment(data);
          editComment(comment);
        } catch (error: any) {
          message.error({
            content:
              error?.response?.data?.message ||
              'Failed to update comment. Please try again.',
            className: 'message-dark-modal'
          });
        }
      },
      setCommentLike: async (data, user) => {
        try {
          await setCommentLike(data, user);
          await assetInternalCommentsSetAssetCommentLike(data);
        } catch (error: any) {
          message.error({
            content:
              error?.response?.data?.message ||
              'Failed to update like status. Please try again.',
            className: 'message-dark-modal'
          });
        }
      },
      setCommentResolve: async (data, user) => {
        await setCommentResolve(data, user);
        await assetInternalCommentsSetAssetCommentResolve(data);
      },
      getPotentialMentions: async ({ assetVersionId, searchQuery }) => {
        const {
          data: { edges }
        } = await assetInternalCommentsListCommentPotentialMentions({
          assetVersionId,
          searchQuery
        });
        if (edges) {
          const list = edges.map((item) => item.node);
          return list;
        }
        return [];
      },
      getPotentialGroupMentions: async ({
        assetVersionId,
        searchQuery
      }: {
        assetVersionId: string;
        searchQuery?: string;
      }) => {
        const {
          data: { edges }
        } =
          await apiClient.asset.assetInternalCommentsControllerListCommentPotentialGroupMentions(
            {
              assetVersionId,
              searchQuery
            }
          );
        if (edges) {
          const list = edges.map((item) => item.node);
          return list;
        }
        return [];
      },
      isLoadingCommentsList,
      commentsList: commentsItems,
      commentsLastReadAt,
      markCommentsRead: async () => {
        if (!assetVersionId) return;
        const optimisticLastReadAt = new Date().toISOString();
        queryClient.setQueriesData(
          { queryKey: [QUERY_COMMENTS_ASSETS, assetVersionId] },
          (prev: GetAssetVersionCommentsSuccessDto | undefined) => {
            if (!prev) return prev;
            return {
              ...prev,
              lastReadAt:
                !prev.lastReadAt ||
                new Date(prev.lastReadAt) < new Date(optimisticLastReadAt)
                  ? optimisticLastReadAt
                  : prev.lastReadAt
            };
          }
        );

        const { data } = await assetInternalCommentsMarkAssetCommentsRead({
          assetVersionId
        });

        queryClient.setQueriesData(
          { queryKey: [QUERY_COMMENTS_ASSETS, assetVersionId] },
          (prev: GetAssetVersionCommentsSuccessDto | undefined) => {
            if (!prev) return prev;
            return {
              ...prev,
              lastReadAt:
                !prev.lastReadAt ||
                new Date(prev.lastReadAt) < new Date(data.lastReadAt)
                  ? data.lastReadAt
                  : prev.lastReadAt
            };
          }
        );
      },
      tasksLastReadAt,
      latestTaskCreatedAt,
      markTasksRead: async () => {
        if (!assetVersionId) return;
        const optimisticLastReadAt = new Date().toISOString();
        queryClient.setQueriesData(
          { queryKey: [QUERY_TASKS_LAST_READ, assetVersionId] },
          (prev: GetAssetVersionTasksLastReadSuccessDto | undefined) => {
            if (!prev) return prev;
            return {
              ...prev,
              lastReadAt:
                !prev.lastReadAt ||
                new Date(prev.lastReadAt) < new Date(optimisticLastReadAt)
                  ? optimisticLastReadAt
                  : prev.lastReadAt
            };
          }
        );

        const { data } = await taskMarkAssetVersionTasksRead({
          assetVersionId
        });

        queryClient.setQueriesData(
          { queryKey: [QUERY_TASKS_LAST_READ, assetVersionId] },
          (prev: GetAssetVersionTasksLastReadSuccessDto | undefined) => {
            if (!prev) return prev;
            return {
              ...prev,
              lastReadAt:
                !prev.lastReadAt ||
                new Date(prev.lastReadAt) < new Date(data.lastReadAt)
                  ? data.lastReadAt
                  : prev.lastReadAt
            };
          }
        );
      },
      workspace: currentWorkspace
    }),
    [
      currentWorkspace,
      isLoadingCommentsList,
      user,
      commentsItems,
      commentsLastReadAt,
      tasksLastReadAt,
      latestTaskCreatedAt,
      assetId,
      assetVersionId,
      queryClient,
      isEmbeddedWebView
    ]
  );

  const assetQuery = useAssetQuery(
    { assetId: assetId ?? '' },
    { enabled: !!assetId }
  );

  const asset = assetQuery.data ?? null;

  const handleEmbeddedGoBack = useCallback(() => {
    if (!closeEmbeddedMediaViewer({ assetId: assetId ?? undefined })) {
      navigate(-1);
    }
  }, [assetId, navigate]);

  useLayoutEffect(() => {
    dispatch(toggleApprovalModal(null));
    dispatch({
      type: MediaViewerActionTypes.SET_RESET_ASSET_STATE
    });
  }, [assetId, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(setAssetsSliderAnchor(null));
    };
  }, [dispatch]);

  useLayoutEffectWithPrev(
    ([prevAsset, prevVersionId]) => {
      dispatch(setAsset(asset));
      if (asset?.workspaceId) {
        setCurrentWorkspace(asset.workspaceId);
      }
      const version =
        asset?.asset?.versions.find((v) => v.id === versionIdParam) ??
        asset?.asset?.versions[0];
      dispatch(setAssetVariant(version?.id ?? null));

      let newVersionAdded = false;
      let newVersionAddedByMe = false;
      if (prevAsset?.id === asset?.id) {
        const optimisticUpdate =
          !!(prevAsset as any)?.versionsOptimisticUpdate ||
          !!(asset as any)?.versionsOptimisticUpdate;
        const prevVersionIds = (prevAsset?.asset?.versions ?? []).map(
          (v) => v.id
        );
        const newVersionIds = (asset?.asset?.versions ?? []).map((v) => v.id);
        newVersionAdded =
          !optimisticUpdate &&
          newVersionIds[0] !== prevVersionId &&
          newVersionIds.some((id) => !prevVersionIds.includes(id));
        if (newVersionAdded) {
          const newVersion = asset?.asset?.versions.find(
            (v) => !prevVersionIds.includes(v.id)
          );
          newVersionAddedByMe = !!newVersion?.user?.me;
        }
      }
      const search = searchParamsRef.current.toString();
      if (asset && version?.id !== versionIdParam) {
        const version = asset?.asset?.versions[0];
        navigate(mediaViewerPath(nonNull(assetId), version?.id ?? '', search), {
          replace: true
        });
      } else if (newVersionAdded) {
        const goToLatestVersion = () =>
          navigate({
            pathname: `/media-viewer/${assetId}`,
            search
          });
        if (newVersionAddedByMe) goToLatestVersion();
        else showNewAssetVersionToast(nonNull(assetId), goToLatestVersion);
      }
    },
    [asset, versionIdParam, dispatch, setCurrentWorkspace, navigate]
  );

  useWebSocketSubscription(async (msg) => {
    if (msg.payload.assetId !== assetId) return;
    if (msg.payload.assetVersionId !== assetVersionId) return;
    if (msg.causedByMe) return;
    await queryClient.invalidateQueries({
      queryKey: [QUERY_COMMENTS_ASSETS, assetVersionId]
    });
  }, 'asset-comment-created');

  useWebSocketSubscription(async (msg) => {
    if (msg.payload.assetId !== assetId) return;
    if (msg.payload.assetVersionId !== assetVersionId) return;
    if (msg.causedByMe) return;
    await queryClient.invalidateQueries({
      queryKey: [QUERY_COMMENTS_ASSETS, assetVersionId]
    });
  }, 'asset-comment-removed');

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.target.assetId,
    async (messages) => {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.payload.target.assetId !== assetId) return;
      await queryClient.invalidateQueries({
        queryKey: [QUERY_COMMENTS_ASSETS, assetVersionId]
      });
    },
    600,
    (msg) => {
      if (msg.payload.target.assetId !== assetId) return false;
      if (!msg.payload.target.commentId) return false;
      return true;
    },
    'attachment-created'
  );

  useWebSocketSubscription(async (msg) => {
    if (msg.payload.target.assetId !== assetId) return;
    if (msg.causedByMe) return;
    await queryClient.invalidateQueries({
      queryKey: [QUERY_COMMENTS_ASSETS, assetVersionId]
    });
  }, 'attachment-removed');

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.assetId,
    async () => {
      await queryClient.invalidateQueries({
        queryKey: [QUERY_COMMENTS_ASSETS, assetVersionId]
      });
    },
    1000,
    (msg) => {
      if (msg.payload.assetId !== assetId) return false;
      if (msg.payload.assetVersionId !== assetVersionId) return false;
      if (msg.causedByMe) return false;
      return true;
    },
    'asset-comment-updated'
  );

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.assetId,
    async (messages) => {
      const msg = messages[messages.length - 1];
      const payload = msg?.payload;
      const isCurrentVersion = payload?.assetVersionId === assetVersionId;
      const isTranscriptionCompleted =
        payload?.reason === 'transcription-completed';
      if (isTranscriptionCompleted) {
        showTranscriptionReadyToast({
          payload,
          showViewButton: !isCurrentVersion,
          onViewTranscript: () => {
            navigate(
              `/media-viewer/${payload?.assetId}/${payload?.assetVersionId}`
            );
          }
        });
      }
      assetQuery.refetch();
    },
    1000,
    (msg) => {
      if (msg.payload.assetId !== assetId) return false;
      if (msg.payload.reason === 'transcription-completed') return true;
      if (msg.payload.reason === 'new-version') return true;
      if (msg.payload.reason === 'conversion-status-changed') return true;
      return !msg.causedByMe;
    },
    'asset-updated'
  );

  return (
    <AssetsSliderProvider>
      {isDemoOrganization && <DemoModePanel />}
      {!isEmbeddedWebView && <MediaViewerTourBanner />}
      <Layout
        style={{
          minHeight: containerHeight,
          background: backgroundColor,
          color: '#fff',
          ...(isDemoOrganization
            ? ({
                '--demo-mode-panel-width': DEMO_MODE_PANEL_HEIGHT
              } as React.CSSProperties)
            : {})
        }}
      >
        <Layout
          className="site-layout media-viewer-layout"
          style={{
            background: backgroundColor
          }}
        >
          {!assetQuery.isError && (
            <Header
              className="site-layout-background b-background-black"
              style={{
                width: '100%',
                padding: 0
              }}
            >
              <MediaViewerHeader
                {...headerProps}
                isEmbeddedWebView={isEmbeddedWebView}
              />
            </Header>
          )}

          <Content style={{ marginTop: 70 }}>
            <div style={{ minHeight: 360, height: '100%' }}>
              {assetQuery.isError ? (
                <div className="media_viewer_content_deleted">
                  <ErrorIcon />
                  <h3>Oops…looks like that media was deleted.</h3>
                  {isEmbeddedWebView && (
                    <Button
                      type="primary"
                      className="media_viewer_btn media_viewer_content_deleted-back"
                      onClick={handleEmbeddedGoBack}
                    >
                      Go back
                    </Button>
                  )}
                </div>
              ) : (
                <PlayerProvider>
                  <Outlet context={outletContext} />
                </PlayerProvider>
              )}
            </div>
          </Content>
        </Layout>
        <ToastContainer
          transition={Slide}
          closeOnClick={false}
          closeButton={false}
          position="top-center"
          theme="dark"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          progressClassName="fancy-progress-bar"
        />
      </Layout>
    </AssetsSliderProvider>
  );
}

export default MediaViewerLayout;
